import {
  useState, useRef, useEffect, useCallback, type FormEvent,
} from 'react';
import { createPortal } from 'react-dom';
import {
  Send, Loader2, AlertTriangle, Maximize2, Minimize2, X, History, Mic,
  Flag, CheckCircle2,
} from 'lucide-react';
import { EvieOrb } from './EvieOrb';
import { KpiCard, NetworkGauge, playSpeechStream, MarkdownText } from '@evi/ui';
import { useAuthStore } from '@/store/auth';
import { useEvieStore, type EvieInquiry } from '@/store/evie';
import {
  sendAdminAssistantMessage,
  confirmAdminAssistantAction,
  synthesizeAdminSpeech,
  type AdminAssistantToolCall,
  type AdminAssistantPendingAction,
  type EvieVisual as EvieVisualSpec,
} from '@/lib/api';
import { EvieBarList } from './EvieBarList';
import { useSpeechRecognition } from './useSpeechRecognition';
import styles from './EvieChat.module.css';

/**
 * Admin EVII chat shell.
 *
 * One always-mounted component with two PRESENTATIONS driven by the store's
 * `view`: a compact floating popup, and a maximized right-side DOCKED panel
 * (the AppShell reflows its content beside it — see AppShell .contentWithEvie).
 * Maximize/minimize only flip `view`; the component stays mounted, so the live
 * conversation (React state below) is preserved across the transition.
 *
 * Rendering mirrors the driver EVII sheet (message bubbles + composer). It talks
 * ONLY to the proven /admin-assistant endpoint — no data access lives here; the
 * boundary stays server-side in the scoped tools.
 */

const EMPTY_INQUIRIES: EvieInquiry[] = [];
const GREETING =
  "Hi, I'm EVII™. Ask me about your network — revenue, energy, sessions, or "
  + 'charger status. I only ever see your own scoped data.';
const EMPTY_REPLY_FALLBACK = 'Sorry, I had trouble responding. Please try again.';

type Msg =
  | { kind: 'user'; text: string; ts: number }
  | {
      kind: 'assistant'; text: string; ts: number;
      toolCalls: AdminAssistantToolCall[];
      /** A drafted action awaiting confirmation; drives the in-chat confirm card. */
      pending_action?: AdminAssistantPendingAction | null;
    }
  | { kind: 'error'; text: string; ts: number };

function newId(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function EvieChat() {
  const view = useEvieStore((s) => s.view);
  const close = useEvieStore((s) => s.close);
  const maximize = useEvieStore((s) => s.maximize);
  const minimize = useEvieStore((s) => s.minimize);
  const recordInquiry = useEvieStore((s) => s.recordInquiry);
  const voiceActive = useEvieStore((s) => s.voiceActive);
  const setVoiceActive = useEvieStore((s) => s.setVoiceActive);
  const toggleVoice = useEvieStore((s) => s.toggleVoice);

  // Per-user memory: read ONLY the current operator's slice, so it can never
  // surface another user's inquiries on a shared device.
  const userId = useAuthStore((s) => s.user?._id ?? null);
  const inquiries = useEvieStore(
    (s) => (userId ? s.inquiriesByUser[userId] ?? EMPTY_INQUIRIES : EMPTY_INQUIRIES),
  );
  // The operator's persisted EVII voice (female | male), default female. Sent to
  // the speak endpoint, which maps it to the Polly voice (Tiffany / Matthew).
  const voiceChoice = useEvieStore(
    (s) => (userId ? s.voiceByUser[userId] ?? 'female' : 'female'),
  );
  // Mic auto-start on open (per-operator, persisted; default on).
  const micAutostart = useEvieStore(
    (s) => (userId ? s.micAutostartByUser[userId] ?? true : true),
  );

  const [messages, setMessages] = useState<Msg[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  // Stable chat id -> keys the server's pending-action stash for confirm.
  const [conversationId] = useState(() => newId());
  const scrollRef = useRef<HTMLDivElement | null>(null);

  // Greet once on first open; keep the thread across close/reopen + maximize.
  useEffect(() => {
    if (view === 'closed' || messages.length > 0) return;
    setMessages([{ kind: 'assistant', text: GREETING, ts: Date.now(), toolCalls: [] }]);
  }, [view, messages.length]);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, sending]);

  const handleSend = useCallback(
    async (text: string): Promise<string | null> => {
      const trimmed = text.trim();
      if (!trimmed || sending) return null;
      setMessages((prev) => [...prev, { kind: 'user', text: trimmed, ts: Date.now() }]);
      setInput('');
      setSending(true);
      try {
        const res = await sendAdminAssistantMessage(trimmed, conversationId);
        const answer = res.answer || EMPTY_REPLY_FALLBACK;
        setMessages((prev) => [
          ...prev,
          {
            kind: 'assistant', text: answer, ts: Date.now(),
            toolCalls: res.tool_calls ?? [],
            pending_action: res.pending_action ?? null,
          },
        ]);
        // Record into the per-user, capped-at-3 recent memory.
        recordInquiry(userId, { id: newId(), question: trimmed, answer, at: Date.now() });
        return answer;
      } catch (err) {
        let message = 'Something went wrong. Please try again.';
        if (err instanceof Error && 'response' in err) {
          try {
            const body = await (err as { response: Response }).response.json();
            if (body?.error?.message) message = String(body.error.message);
          } catch { /* keep the generic message */ }
        }
        setMessages((prev) => [...prev, { kind: 'error', text: message, ts: Date.now() }]);
        return null;
      } finally {
        setSending(false);
      }
    },
    [sending, userId, recordInquiry, conversationId],
  );

  // ── Action confirmation (propose -> confirm -> execute, Actions Phase 2) ──
  // Confirm or cancel a drafted action. On confirm the server re-checks the
  // permission + runs the SAME guarded write a human uses, as the operator.
  const handleConfirmAction = useCallback(
    async (token: string, decision: 'confirm' | 'cancel'): Promise<string | null> => {
      // Drop the card from the proposing message so it can't double-submit.
      setMessages((prev) => prev.map((mm) =>
        mm.kind === 'assistant' && mm.pending_action?.token === token
          ? { ...mm, pending_action: null }
          : mm));
      try {
        const res = await confirmAdminAssistantAction(conversationId, token, decision);
        setMessages((prev) => [
          ...prev,
          { kind: 'assistant', text: res.message, ts: Date.now(), toolCalls: [] },
        ]);
        return res.message;
      } catch {
        setMessages((prev) => [
          ...prev,
          { kind: 'error', text: 'Could not complete that just now. Please try again.', ts: Date.now() },
        ]);
        return null;
      }
    },
    [conversationId],
  );

  // The latest drafted action still awaiting confirmation, for the spoken-confirm
  // path (held in a ref so the stable voice loop sees the current value).
  let pendingActionToken: string | null = null;
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const mm = messages[i];
    if (mm && mm.kind === 'assistant' && mm.pending_action) {
      pendingActionToken = mm.pending_action.token;
      break;
    }
  }
  const pendingActionTokenRef = useRef<string | null>(null);
  useEffect(() => { pendingActionTokenRef.current = pendingActionToken; }, [pendingActionToken]);

  // ── Voice mode: hands-free browser STT -> handleSend -> Polly TTS -> repeat.
  //    The mic is live ONLY during the user's turn: it is paused while the
  //    request is in flight and while EVII's answer plays, so it never captures
  //    EVII's own voice (the echo problem). Both the spoken question and the
  //    answer flow through handleSend, so they become normal chat messages --
  //    voice shares the transcript with text. ──
  const voiceActiveRef = useRef(voiceActive);
  useEffect(() => { voiceActiveRef.current = voiceActive; }, [voiceActive]);
  // Latest voice choice for use inside the stable speak() callback.
  const voiceChoiceRef = useRef(voiceChoice);
  useEffect(() => { voiceChoiceRef.current = voiceChoice; }, [voiceChoice]);

  const stopSpeakRef = useRef<(() => void) | null>(null);
  const startListenRef = useRef<() => void>(() => {});
  const stopListenRef = useRef<() => void>(() => {});

  // Stop any in-flight playback (e.g. on toggle-off); resolves the pending speak().
  const stopAudio = useCallback(() => {
    stopSpeakRef.current?.();
    stopSpeakRef.current = null;
    setSpeaking(false);
  }, []);

  // Synthesize via Polly and play it AS IT STREAMS (playSpeechStream) so EVII
  // starts speaking after the first chunk, not the whole clip. Resolves when
  // playback ends, errors, or is stopped -- so the loop always continues.
  const speak = useCallback(async (text: string): Promise<void> => {
    try {
      const response = await synthesizeAdminSpeech(text, voiceChoiceRef.current);
      if (!voiceActiveRef.current) {
        try { await response.body?.cancel(); } catch { /* ignore */ }
        return; // toggled off while synthesizing
      }
      const playback = playSpeechStream(response);
      stopSpeakRef.current = playback.stop;
      setSpeaking(true);
      await playback.done;
    } catch {
      /* synth/playback failure: skip speaking this answer, keep the loop alive */
    } finally {
      stopSpeakRef.current = null;
      setSpeaking(false);
    }
  }, []);

  // One full turn: send the spoken transcript through the normal flow, speak the
  // answer, then resume listening if still in voice mode.
  const handleVoiceTranscript = useCallback(async (transcript: string) => {
    if (!voiceActiveRef.current) return;
    // Spoken confirm/cancel when a drafted action awaits confirmation: a "yes"
    // confirms, a "no" cancels, anything else is a normal message.
    const token = pendingActionTokenRef.current;
    if (token) {
      const t = transcript.trim().toLowerCase();
      const yes = /^(yes|yeah|yep|confirm|confirmed|go ahead|do it|okay|ok)\b/.test(t);
      const no = /^(no|nope|cancel|don'?t|do not|stop|never ?mind)\b/.test(t);
      if (yes || no) {
        const msg = await handleConfirmAction(token, yes ? 'confirm' : 'cancel');
        if (msg && voiceActiveRef.current) await speak(msg);
        if (voiceActiveRef.current) startListenRef.current();
        return;
      }
    }
    const answer = await handleSend(transcript);
    if (!voiceActiveRef.current) return;
    if (answer) await speak(answer);
    if (voiceActiveRef.current) startListenRef.current();
  }, [handleSend, speak, handleConfirmAction]);

  const recognition = useSpeechRecognition(handleVoiceTranscript);
  // Stash the recognizer controls in refs so the loop + effects call them without
  // depending on their identity (which would cause spurious restarts).
  useEffect(() => {
    startListenRef.current = recognition.start;
    stopListenRef.current = recognition.stop;
  }, [recognition.start, recognition.stop]);
  const recognitionSupported = recognition.supported;

  // Enter/exit voice mode: start listening on, stop the mic + any audio on off.
  useEffect(() => {
    if (!recognitionSupported) return;
    if (voiceActive) startListenRef.current();
    else { stopListenRef.current(); stopAudio(); }
  }, [voiceActive, recognitionSupported, stopAudio]);

  // Closing the chat ends voice mode -- never leave the mic live (or the icon
  // glowing) behind a closed chat.
  useEffect(() => {
    if (view === 'closed' && voiceActive) setVoiceActive(false);
  }, [view, voiceActive, setVoiceActive]);

  // Auto-start voice when the chat OPENS, if the operator's auto-start pref is
  // on (the default). Fires once per open (a ref tracks the transition), only
  // when recognition is supported; setting voiceActive triggers the voice
  // effect above, which starts the mic (prompting for permission the first time).
  const autostartedRef = useRef(false);
  useEffect(() => {
    if (view === 'closed') { autostartedRef.current = false; return; }
    if (autostartedRef.current) return;
    autostartedRef.current = true;
    if (recognitionSupported && micAutostart && !voiceActiveRef.current) {
      setVoiceActive(true);
    }
  }, [view, recognitionSupported, micAutostart, setVoiceActive]);

  // Final safety: stop the mic + any audio if the shell unmounts.
  useEffect(() => () => { stopListenRef.current(); stopAudio(); }, [stopAudio]);

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    void handleSend(input);
  };

  if (view === 'closed') return null;
  const isPanel = view === 'panel';

  return createPortal(
    <div
      className={`${styles.shell} ${isPanel ? styles.panel : styles.popup}`}
      role="dialog"
      aria-label="EVII™ assistant"
    >
      <header className={styles.header}>
        <div className={styles.title}><EvieOrb size={16} /><span>EVII™</span></div>
        <div className={styles.headerActions}>
          {isPanel ? (
            <button
              type="button" className={`${styles.headerBtn} ${styles.resizeBtn}`} onClick={minimize}
              aria-label="Minimize to popup" title="Minimize to popup"
            >
              <Minimize2 size={15} />
            </button>
          ) : (
            <button
              type="button" className={`${styles.headerBtn} ${styles.resizeBtn}`} onClick={maximize}
              aria-label="Maximize to side panel" title="Maximize to side panel"
            >
              <Maximize2 size={15} />
            </button>
          )}
          <button
            type="button" className={styles.headerBtn} onClick={close}
            aria-label="Close" title="Close"
          >
            <X size={16} />
          </button>
        </div>
      </header>

      {inquiries.length > 0 && (
        <div className={styles.recent}>
          <span className={styles.recentLabel}><History size={12} /> Recent</span>
          <div className={styles.recentChips}>
            {inquiries.map((q) => (
              <button
                key={q.id}
                type="button"
                className={styles.recentChip}
                title={q.question}
                onClick={() => void handleSend(q.question)}
              >
                {q.question.length > 44 ? `${q.question.slice(0, 44)}…` : q.question}
              </button>
            ))}
          </div>
        </div>
      )}

      <div ref={scrollRef} className={styles.transcript} aria-live="polite">
        {messages.map((m, i) => (
          <MessageRow key={`${m.ts}-${i}`} m={m} onConfirmAction={handleConfirmAction} />
        ))}
        {sending && (
          <div className={styles.typing}>
            <Loader2 size={14} className={styles.spin} /><span>Thinking…</span>
          </div>
        )}
      </div>

      {voiceActive && (
        <div className={styles.voiceBar}>
          <span
            className={`${styles.voiceDot} ${
              speaking
                ? styles.voiceDotSpeaking
                : recognition.listening
                  ? styles.voiceDotListening
                  : ''
            }`}
          />
          <span className={styles.voiceStatus}>
            {speaking
              ? 'Speaking…'
              : sending
                ? 'Thinking…'
                : recognition.listening
                  ? (recognition.interimText || 'Listening…')
                  : 'Voice on'}
          </span>
          <button
            type="button"
            className={styles.voiceStop}
            onClick={() => setVoiceActive(false)}
          >
            Stop
          </button>
        </div>
      )}

      <form onSubmit={onSubmit} className={styles.composer}>
        {/* Mic toggle — at the bottom in the composer (matches the driver app).
            Hidden only where the browser has no speech recognition (e.g. Firefox). */}
        {recognitionSupported && (
          <button
            type="button"
            className={`${styles.micBtn} ${voiceActive ? styles.micBtnActive : ''}`}
            onClick={toggleVoice}
            aria-label={voiceActive ? 'Turn off voice mode' : 'Turn on voice mode'}
            aria-pressed={voiceActive}
            title={voiceActive ? 'Voice mode on — tap to stop' : 'Voice mode'}
          >
            <Mic size={16} />
          </button>
        )}
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Ask EVII™ about your network…"
          maxLength={2000}
          disabled={sending}
          className={styles.input}
          aria-label="Message"
        />
        <button
          type="submit"
          disabled={sending || !input.trim()}
          className={styles.send}
          aria-label="Send"
        >
          <Send size={16} />
        </button>
      </form>
      <p className={styles.scopeHint}>EVII™ only sees your own scoped data.</p>
    </div>,
    document.body,
  );
}

function MessageRow({ m, onConfirmAction }: {
  m: Msg;
  onConfirmAction: (token: string, decision: 'confirm' | 'cancel') => void;
}) {
  if (m.kind === 'user') {
    return <div className={`${styles.bubble} ${styles.user}`}>{m.text}</div>;
  }
  if (m.kind === 'error') {
    return (
      <div className={`${styles.bubble} ${styles.error}`}>
        <div className={styles.bubbleHead}><AlertTriangle size={13} /><span>Error</span></div>
        <p className={styles.bubbleText}>{m.text}</p>
      </div>
    );
  }
  const pendingAction = m.pending_action ?? null;
  return (
    <div className={`${styles.bubble} ${styles.assistant}`}>
      <div className={styles.bubbleHead}><EvieOrb size={13} /><span>EVII™</span></div>
      {m.toolCalls.length > 0 && (
        <div className={styles.tools}>
          {m.toolCalls.map((tc, i) => (
            <span key={i} className={styles.toolPill} title={tc.result_summary}>{tc.name}</span>
          ))}
        </div>
      )}
      <MarkdownText className={styles.bubbleText} text={m.text} />
      {/* Inline visuals beneath the text — one per tool call that carries a
          visual spec. The text answer always stays; the widget is added, not
          substituted. */}
      {m.toolCalls.map((tc, i) =>
        tc.visual ? <EvieVisual key={`v-${m.ts}-${i}`} visual={tc.visual} /> : null,
      )}
      {/* Reporting slice 2 — report affordances: open the same report in
          the Reports page (the slice 1 URL state) and download the small
          server-built aggregate CSV. Same scoped data as the answer. */}
      {m.toolCalls.map((tc, i) => (tc.report_url || tc.csv) ? (
        <div key={`r-${m.ts}-${i}`} style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
          {tc.report_url && (
            <a
              href={tc.report_url}
              style={{
                fontSize: 12, fontWeight: 600, padding: '6px 12px', borderRadius: 8,
                border: '1px solid var(--color-brd)', color: 'var(--color-tx)',
                textDecoration: 'none',
              }}
            >
              Open this report
            </a>
          )}
          {tc.csv && (
            <button
              type="button"
              style={{
                fontSize: 12, fontWeight: 600, padding: '6px 12px', borderRadius: 8,
                border: '1px solid var(--color-brd)', background: 'transparent',
                color: 'var(--color-tx)', cursor: 'pointer',
              }}
              onClick={() => {
                const csv = tc.csv!;
                const blob = new Blob([csv.content], { type: 'text/csv;charset=utf-8' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = csv.filename;
                a.click();
                URL.revokeObjectURL(url);
              }}
            >
              Download CSV
            </button>
          )}
        </div>
      ) : null)}
      {/* Confirm-before-execute card (Actions Phase 2). EVII proposed a role-
          gated change; it runs only when the operator explicitly confirms. */}
      {pendingAction && (
        <div className={styles.confirmCard}>
          <div className={styles.confirmHead}>
            <Flag size={13} /> <span>{pendingAction.title}</span>
          </div>
          <p className={styles.confirmSummary}>{pendingAction.summary}</p>
          <p className={styles.confirmDetail}>{pendingAction.detail}</p>
          <div className={styles.confirmActions}>
            <button
              type="button"
              className={styles.confirmYes}
              onClick={() => onConfirmAction(pendingAction.token, 'confirm')}
            >
              <CheckCircle2 size={13} /> {pendingAction.confirmLabel ?? 'Confirm'}
            </button>
            <button
              type="button"
              className={styles.confirmNo}
              onClick={() => onConfirmAction(pendingAction.token, 'cancel')}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Renders a tool's visual spec with the matching EXISTING widget — KpiCard or
 * NetworkGauge from @evi/ui (the same components the dashboard uses), or the
 * small EvieBarList. The kind is chosen server-side by the tool; this only maps
 * kind -> widget. No data access here — `visual.data` is a reshape of data the
 * scoped tool already returned, so the boundary stays server-side.
 */
function EvieVisual({ visual }: { visual: EvieVisualSpec }) {
  if (visual.kind === 'kpi') {
    return (
      <div className={styles.visual}>
        <KpiCard
          label={visual.data.label}
          value={visual.data.value}
          {...(visual.data.meta ? { meta: visual.data.meta } : {})}
        />
      </div>
    );
  }
  if (visual.kind === 'network_gauge') {
    return (
      <div className={`${styles.visual} ${styles.visualCenter}`}>
        <NetworkGauge
          online={visual.data.online}
          total={visual.data.total}
          faulted={visual.data.faulted}
          size={150}
        />
      </div>
    );
  }
  // bar_list
  return (
    <div className={styles.visual}>
      <EvieBarList bars={visual.data.bars} />
    </div>
  );
}
